/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Homepage motion (ported from the static prototype's home.js).
 *
 * Scroll      Lenis (shared, see smooth-scroll.ts) drives GSAP ScrollTrigger.
 * Chapters    01-07 share one pin. Each step is a single overlapping crossfade
 *             (blur + rise + word-mask headline) scrubbed to scroll, then settles
 *             on a chapter with a soft snap. Data inside a panel (bars, counters,
 *             LEDs) is time-based, so it plays at its own graceful pace.
 * Ambient     Canvas gutter rails with a scroll gauge, cursor spotlight on panels.
 * Fallbacks   Reduced motion: everything is simply shown.
 *
 * `initHome()` returns a cleanup function that reverts every tween/trigger, removes
 * listeners and stops the canvas loop, so the page can mount/unmount (route changes,
 * React strict mode) without leaking.
 */
import { prefersReducedMotion, registerGsap } from "./gsap";
import { acquireLenis, easeInOutCubic, releaseLenis } from "./smooth-scroll";
import { makeParallax } from "./parallax";

export function initHome(): () => void {
  const reduced = prefersReducedMotion();
  const ac = new AbortController();
  const { signal } = ac;
  const timers = new Set<number>();
  const later = (fn: () => void, ms: number) => {
    const id = window.setTimeout(() => {
      timers.delete(id);
      fn();
    }, ms);
    timers.add(id);
    return id;
  };

  const lines = Array.from(document.querySelectorAll<HTMLElement>(".smtp-line"));
  const cleanups: Array<() => void> = [];
  const reveal = () => lines.forEach((l) => (l.style.opacity = "1"));

  /* ------------------------------------------------------------------
     Small semantic markers used by CSS (client lines, warm state)
     ------------------------------------------------------------------ */
  function markContent() {
    document.querySelectorAll(".smtp-term-body .dir").forEach((d) => {
      if (d.textContent?.trim() === "C") {
        d.classList.add("is-c");
        return;
      }
      /* Server reply: colour the 3-digit status code, like a log level */
      const tn = d.nextSibling;
      const m = tn && tn.nodeType === 3 && (tn.nodeValue || "").match(/^(\d{3})([\s\S]*)$/);
      if (m && tn && tn.parentNode) {
        const code = document.createElement("span");
        code.className = "code";
        code.textContent = m[1];
        tn.nodeValue = m[2];
        tn.parentNode.insertBefore(code, tn);
      }
    });
    document.querySelectorAll(".home-panel-row:not(.head)").forEach((row) => {
      const last = row.lastElementChild;
      if (last && /warm/i.test(last.textContent || "")) row.classList.add("is-warm");
    });
  }

  /* ------------------------------------------------------------------
     Dedicated-IP calculator (works with or without GSAP)
     ------------------------------------------------------------------ */
  function initCalculator(animate: boolean) {
    const tiers = [
      { ips: "1 address", price: 39, per: "€0.195 / 1k" },
      { ips: "2 addresses", price: 77, per: "€0.192 / 1k" },
      { ips: "3 addresses", price: 114, per: "€0.190 / 1k" },
      { ips: "4 addresses", price: 150, per: "€0.187 / 1k" },
      { ips: "5 addresses", price: 185, per: "€0.185 / 1k" },
      { ips: "6 addresses", price: 219, per: "€0.182 / 1k" },
      { ips: "7 addresses", price: 252, per: "€0.180 / 1k" },
      { ips: "8 addresses", price: 284, per: "€0.177 / 1k" },
      { ips: "9 addresses", price: 315, per: "€0.175 / 1k" },
      { ips: "10 addresses", price: 345, per: "€0.172 / 1k" },
    ];
    const range = document.getElementById("home-ip-range") as HTMLInputElement | null;
    if (!range) return;
    const elLabel = document.getElementById("home-ips-label")!;
    const elIps = document.getElementById("home-ips")!;
    const elEmails = document.getElementById("home-emails")!;
    const elPrice = document.getElementById("home-price")!;
    const elPer = document.getElementById("home-per")!;
    const rack = (document.getElementById("home-ip-rack") as HTMLElement).children;
    const cur = { ips: 1, emails: 100000, price: 39 };
    let prev = 1;
    const gsap = animate ? registerGsap().gsap : null;

    const write = () => {
      elIps.textContent = String(Math.round(cur.ips));
      elEmails.textContent = Math.round(cur.emails).toLocaleString("en-US");
      elPrice.textContent = "€" + Math.round(cur.price);
    };

    const render = (first: boolean) => {
      const n = Number(range.value);
      const t = tiers[n - 1];
      range.style.setProperty("--p", ((n - 1) / 9) * 100 + "%");
      range.setAttribute("aria-valuenow", range.value);
      elLabel.textContent = t.ips;
      elPer.textContent = t.per + " · no daily cap";

      for (let i = 0; i < rack.length; i++) {
        const on = i < n;
        const wasOn = i < prev;
        const el = rack[i] as HTMLElement;
        if (on !== wasOn && !first) {
          const steps = on ? i - prev + 1 : prev - i;
          el.style.transitionDelay = Math.max(0, steps - 1) * 34 + "ms";
        } else {
          el.style.transitionDelay = "0ms";
        }
        el.classList.toggle("on", on);
      }
      prev = n;

      if (gsap && !first) {
        gsap.to(cur, { ips: n, emails: n * 100000, price: t.price, duration: 0.9, ease: "power3.out", overwrite: true, onUpdate: write });
      } else {
        cur.ips = n;
        cur.emails = n * 100000;
        cur.price = t.price;
        write();
      }
    };

    range.addEventListener("input", () => render(false), { signal });
    render(true);
  }

  /* ---------------------------------------------------------------- boot */
  markContent();
  initCalculator(!reduced);

  if (reduced) {
    reveal();
    return teardown();
  }

  const { gsap, ScrollTrigger } = registerGsap();
  const lenis = acquireLenis();
  cleanups.push(() => releaseLenis());

  const ease = "expo.out";
  const pinNav: { tl: any; st: any; points: number[]; acts: number[] } = { tl: null, st: null, points: [], acts: [] };
  let dotIndex = -1;
  let snapBusy = false;
  let lastWheel = 0;
  let snapTimer = 0;
  let lastScrollY = window.pageYOffset;
  let scrollDir = 1;

  function scrollToY(y: number, duration = 1.2, lock = false) {
    y = Math.max(0, y);
    snapBusy = true;
    later(() => (snapBusy = false), duration * 1000 + 250);
    if (lenis) {
      lenis.scrollTo(y, {
        duration,
        easing: easeInOutCubic,
        lock,
        onComplete: () => {
          snapBusy = false;
        },
      });
      return;
    }
    const proxy = { y: window.pageYOffset };
    gsap.to(proxy, { y, duration, ease: "power3.inOut", overwrite: true, onUpdate: () => window.scrollTo(0, proxy.y) });
  }

  /* ------------------------------------------------------------------
     Headline word masks
     ------------------------------------------------------------------ */
  function splitWords(el: (HTMLElement & { __words?: HTMLElement[] }) | null): HTMLElement[] {
    if (!el) return [];
    if (el.__words) return el.__words;
    el.setAttribute("aria-label", (el.textContent || "").replace(/\s+/g, " ").trim());
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    const nodes: Node[] = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    nodes.forEach((node) => {
      const parts = (node.nodeValue || "").split(/(\s+)/);
      const frag = document.createDocumentFragment();
      parts.forEach((part) => {
        if (!part) return;
        if (/^\s+$/.test(part)) {
          frag.appendChild(document.createTextNode(" "));
          return;
        }
        const w = document.createElement("span");
        w.className = "w";
        w.setAttribute("aria-hidden", "true");
        const wi = document.createElement("span");
        wi.className = "wi";
        wi.textContent = part;
        w.appendChild(wi);
        frag.appendChild(w);
      });
      node.parentNode!.replaceChild(frag, node);
    });
    el.__words = Array.from(el.querySelectorAll<HTMLElement>(".wi"));
    return el.__words;
  }

  /* Count-up for the numeric text node at the start of an element */
  function makeCount(el: Element) {
    const tn = el.firstChild;
    if (!tn || tn.nodeType !== 3) return null;
    /* Remember the authored text so a re-init (strict mode, HMR) never reads a zeroed value */
    const source = el.getAttribute("data-count-src") ?? (tn.nodeValue || "");
    el.setAttribute("data-count-src", source);
    const m = source.match(/^([^\d]*)(\d[\d,]*(?:\.\d+)?)(.*)$/);
    if (!m) return null;
    const num = parseFloat(m[2].replace(/,/g, ""));
    const dec = (m[2].split(".")[1] || "").length;
    const comma = m[2].indexOf(",") > -1;
    const c = {
      state: { v: 0 },
      to: num,
      write(v: number) {
        const s = comma ? v.toLocaleString("en-US", { minimumFractionDigits: dec, maximumFractionDigits: dec }) : v.toFixed(dec);
        tn.nodeValue = m[1] + s + m[3];
      },
      update() {
        c.write(c.state.v);
      },
      reset() {
        c.state.v = 0;
        c.write(0);
      },
    };
    c.write(0);
    return c;
  }

  const rightOf = (stage: Element) => stage.querySelector<HTMLElement>(".home-object, .home-band-grid, .home-steps, .home-quotes");

  /* ------------------------------------------------------------------
     Panel details: time-based, replayed whenever a chapter becomes active
     ------------------------------------------------------------------ */
  function buildDetails(stage: Element) {
    const tl = gsap.timeline({ paused: true });
    const counts: Array<ReturnType<typeof makeCount>> = [];
    let any = false;
    const q = (sel: string) => stage.querySelectorAll(sel);
    const has = (list: NodeListOf<Element> | null) => !!(list && list.length);

    const bars = q(".home-chart-bar");
    if (has(bars)) {
      any = true;
      tl.from(q(".home-chart-lab"), { autoAlpha: 0, x: -12, duration: 0.8, stagger: 0.07, ease: "power3.out" }, 0.05);
      tl.from(bars, { scaleX: 0, transformOrigin: "left center", duration: 1.5, stagger: 0.08, ease: "expo.out" }, 0.12);
      tl.from(q(".home-chart-axis span"), { autoAlpha: 0, duration: 0.8, stagger: 0.06 }, 0.5);
      q(".home-chart-val").forEach((v, i) => {
        const c = makeCount(v);
        if (!c) return;
        counts.push(c);
        tl.to(c.state, { v: c.to, duration: 1.5, ease: "expo.out", onUpdate: c.update }, 0.12 + i * 0.08);
      });
    }

    const cells = q(".home-panel-stats article");
    if (has(cells)) {
      any = true;
      tl.from(cells, { y: 18, autoAlpha: 0, duration: 0.9, stagger: 0.09, ease: "expo.out" }, 0.05);
      q(".home-panel-stats strong").forEach((s, i) => {
        const c = makeCount(s);
        if (!c) return;
        counts.push(c);
        tl.to(c.state, { v: c.to, duration: 1.7, ease: "expo.out", onUpdate: c.update }, 0.15 + i * 0.09);
      });
      tl.from(q(".home-panel-row"), { y: 12, autoAlpha: 0, duration: 0.8, stagger: 0.08, ease: "expo.out" }, 0.45);
    }

    const rows = q(".home-sheet-row, .home-proc-row");
    if (has(rows)) {
      any = true;
      tl.from(rows, { y: 22, autoAlpha: 0, duration: 1, stagger: 0.14, ease: "expo.out" }, 0.1);
    }

    const ip = stage.querySelector(".home-ip");
    if (ip) {
      any = true;
      const s = (sel: string) => ip.querySelectorAll(sel);
      tl.from(s("#home-ip-rack span"), { scaleY: 0, transformOrigin: "bottom center", duration: 0.9, stagger: 0.045, ease: "expo.out" }, 0.05);
      tl.from(s(".home-ip-readout div"), { y: 14, autoAlpha: 0, duration: 0.8, stagger: 0.08, ease: "expo.out" }, 0.3);
      tl.from(s(".home-calc-range-label, #home-ip-range, .home-calc-ticks"), { y: 12, autoAlpha: 0, duration: 0.8, stagger: 0.07, ease: "expo.out" }, 0.5);
      tl.from(s(".home-ip-per"), { y: 10, autoAlpha: 0, duration: 0.7, ease: "expo.out" }, 0.62);
      tl.from(s(".home-ip-spec li"), { y: 10, autoAlpha: 0, duration: 0.7, stagger: 0.06, ease: "expo.out" }, 0.7);
      tl.from(s(".home-ip-cta > *, .home-ip-pay"), { y: 12, autoAlpha: 0, duration: 0.8, stagger: 0.08, ease: "expo.out" }, 0.9);
    }

    if (!any) {
      tl.kill();
      return null;
    }
    return {
      play: () => tl.restart(),
      reset: () => {
        tl.pause(0);
        counts.forEach((c) => c && c.reset());
      },
    };
  }

  /* ------------------------------------------------------------------
     Chapter rail
     ------------------------------------------------------------------ */
  function setDot(index: number) {
    if (index === dotIndex) return;
    dotIndex = index;
    const nav = document.getElementById("home-dots");
    if (!nav) return;
    const btns = nav.querySelectorAll("button");
    if (btns[index]) nav.setAttribute("data-accent", btns[index].getAttribute("data-accent") || "signal");
    btns.forEach((b, i) => {
      b.classList.toggle("is-active", i === index);
      b.setAttribute("aria-current", i === index ? "true" : "false");
    });
    const fill = nav.querySelector(".home-dots-line i");
    if (fill && btns.length > 1) {
      gsap.to(fill, { height: (index / (btns.length - 1)) * 100 + "%", duration: 0.9, ease: "expo.out", overwrite: true });
    }
  }

  function chapterY(index: number) {
    const { st, tl } = pinNav;
    if (!st || !tl || tl.labels["ch-" + index] == null) return null;
    return st.start + (tl.labels["ch-" + index] / (tl.duration() || 1)) * (st.end - st.start);
  }

  function goToChapter(index: number) {
    const stages = document.querySelectorAll<HTMLElement>("#home-pin .home-stage");
    const close = document.querySelector<HTMLElement>(".home-stage-end");
    const from = Math.max(0, dotIndex);
    const dur = Math.min(2.6, 1 + Math.abs(index - from) * 0.28);
    if (index >= 7 && close) {
      scrollToY(close.getBoundingClientRect().top + window.pageYOffset - 72, dur);
      return;
    }
    const y = chapterY(index);
    if (y != null) {
      scrollToY(y, dur);
      return;
    }
    if (stages[index]) scrollToY(stages[index].getBoundingClientRect().top + window.pageYOffset - 72, dur);
  }

  function setupDots() {
    const nav = document.getElementById("home-dots");
    if (!nav) return;
    nav.querySelectorAll("button").forEach((btn) => {
      btn.addEventListener("click", () => goToChapter(Number(btn.getAttribute("data-ch"))), { signal });
    });
    setDot(0);
    gsap.from(nav.querySelectorAll("button"), { autoAlpha: 0, x: 18, duration: 1.1, stagger: 0.06, delay: 0.9, ease });
    const line = nav.querySelector(".home-dots-line");
    if (line) gsap.from(line, { scaleY: 0, transformOrigin: "top center", duration: 1.4, delay: 0.8, ease });
  }

  /* ------------------------------------------------------------------
     Hero intro
     ------------------------------------------------------------------ */
  function introHero() {
    const hero = document.querySelector<HTMLElement>(".home-stage-hero");
    if (!hero) return;
    const words = splitWords(hero.querySelector("h1"));
    const idx = hero.querySelector(".home-idx");
    const lead = hero.querySelector(".lead");
    const actions = hero.querySelector(".hero-actions");
    const specs = hero.querySelectorAll(".home-hero-specs li");
    const object = hero.querySelector(".home-object");
    const routeEl = hero.querySelector(".home-route");
    const route = hero.querySelectorAll(".home-route-node, .home-route-span");
    const nav = document.querySelector(".nav-bar");

    gsap.set(words, { yPercent: 118 });
    gsap.set([idx, lead, actions], { autoAlpha: 0, y: 22 });
    gsap.set(specs, { autoAlpha: 0, y: 14 });
    gsap.set(route, { autoAlpha: 0, y: 12 });
    if (object) gsap.set(object, { autoAlpha: 0, y: 56, clipPath: "inset(0% 0% 100% 0%)" });
    if (lines.length) gsap.set(lines, { opacity: 1, clipPath: "inset(0 100% 0 0)" });

    const tl = gsap.timeline({ defaults: { ease } });
    if (nav) tl.from(nav, { y: -28, autoAlpha: 0, duration: 1.4, clearProps: "transform,opacity,visibility" }, 0);
    tl.to(idx, { autoAlpha: 1, y: 0, duration: 1.1 }, 0.15);
    tl.to(words, { yPercent: 0, duration: 1.5, stagger: 0.085 }, 0.25);
    tl.to(lead, { autoAlpha: 1, y: 0, duration: 1.2 }, 0.75);
    tl.to(actions, { autoAlpha: 1, y: 0, duration: 1.2 }, 0.9);
    tl.to(specs, { autoAlpha: 1, y: 0, duration: 1, stagger: 0.07 }, 1.1);
    if (object) tl.to(object, { autoAlpha: 1, y: 0, clipPath: "inset(0% 0% 0% 0%)", duration: 1.7, clearProps: "clipPath" }, 0.4);
    tl.to(route, { autoAlpha: 1, y: 0, duration: 0.9, stagger: 0.08 }, 1.25);
    if (routeEl) tl.call(() => routeEl.classList.add("is-live"), undefined, 2.4);

    let t = 1.7;
    let lastLine: HTMLElement | null = null;
    for (const line of lines) {
      const client = !!line.querySelector(".dir.is-c");
      const d = client ? 0.6 : 0.34;
      tl.to(line, { clipPath: "inset(0 0% 0 0)", duration: d, ease: client ? "steps(24)" : "power2.out", clearProps: "clipPath" }, t);
      t += d + 0.16;
      lastLine = line;
    }
    if (lastLine) {
      const target = lastLine;
      tl.call(
        () => {
          if (target.querySelector(".smtp-caret")) return;
          const caret = document.createElement("span");
          caret.className = "smtp-caret";
          caret.setAttribute("aria-hidden", "true");
          target.appendChild(caret);
        },
        undefined,
        t
      );
    }
  }

  /* ------------------------------------------------------------------
     Desktop: pinned chapters
     ------------------------------------------------------------------ */
  function pinSplitChapters() {
    const pin = document.getElementById("home-pin");
    if (!pin) return;
    const stageList = pin.querySelectorAll<HTMLElement>(".home-stage");
    if (stageList.length < 2) return;
    const stages = Array.from(stageList);
    const n = stages.length;
    const allQuotes = pin.querySelectorAll<HTMLElement>(".home-quote");
    const extraQuotes = Math.max(0, allQuotes.length - 1);

    pin.classList.add("is-active");
    /* Pin from scroll 0 so the very first wheel notch already belongs to chapter 1 */
    if (pin.parentElement) pin.parentElement.classList.add("is-pinned");

    const parts = stages.map((stage) => {
      const copy = stage.querySelector<HTMLElement>(".home-copy");
      const obj = rightOf(stage);
      const heading = copy && copy.querySelector<HTMLElement>("h1, h2");
      return {
        stage,
        copy,
        obj,
        words: heading ? splitWords(heading) : [],
        idx: copy && copy.querySelector(".home-idx"),
        lead: copy && copy.querySelector(".lead"),
        meter: copy && copy.querySelector(".home-copy-meter"),
        actions: copy && copy.querySelector(".hero-actions"),
        details: buildDetails(stage),
      };
    });

    /* Initial states */
    parts.forEach((p, i) => {
      gsap.set(p.stage, { zIndex: i === 0 ? 2 : 1 });
      if (p.copy) gsap.set(p.copy, { filter: "blur(0px)" });
      if (p.obj) gsap.set(p.obj, { filter: "blur(0px)" });
      if (i === 0) return;
      if (p.copy) gsap.set(p.copy, { autoAlpha: 0 });
      if (p.obj) gsap.set(p.obj, { autoAlpha: 0, y: 64, scale: 0.97, filter: "blur(8px)" });
      gsap.set(p.words, { yPercent: 118 });
      gsap.set([p.idx, p.lead, p.meter, p.actions].filter(Boolean), { autoAlpha: 0, y: 18 });
    });
    for (let qi = 1; qi < allQuotes.length; qi++) {
      gsap.set(allQuotes[qi], { y: 48, autoAlpha: 0, filter: "blur(6px)" });
    }
    if (allQuotes[0]) gsap.set(allQuotes[0], { filter: "blur(0px)" });

    let activeDetail = -1;
    const tl: any = gsap.timeline({
      defaults: { ease: "none" },
      scrollTrigger: {
        trigger: pin,
        start: "top top",
        end: () => "+=" + window.innerHeight * (n + extraQuotes) * 0.9,
        pin: true,
        pinSpacing: true,
        scrub: lenis ? true : 0.8,
        anticipatePin: 1,
        invalidateOnRefresh: true,
        onUpdate: onPinUpdate,
      },
    });

    let pos = 0;
    const points = [0];
    const acts = [0];
    tl.addLabel("ch-0", 0);

    for (let i = 0; i < n - 1; i++) {
      const T = pos;
      const cur = parts[i];
      const nxt = parts[i + 1];

      /* Out: the current chapter lifts away and softens. */
      if (cur.copy) {
        tl.fromTo(
          cur.copy,
          { y: 0, autoAlpha: 1, filter: "blur(0px)" },
          { y: -34, autoAlpha: 0, filter: "blur(8px)", duration: 0.38, ease: "power2.in", immediateRender: false },
          T
        );
      }
      if (cur.obj) {
        tl.fromTo(
          cur.obj,
          { y: 0, scale: 1, autoAlpha: 1, filter: "blur(0px)" },
          { y: -52, scale: 0.985, autoAlpha: 0, filter: "blur(10px)", duration: 0.42, ease: "power2.in", immediateRender: false },
          T
        );
      }
      tl.set(nxt.stage, { zIndex: 3 + i }, T + 0.4);
      tl.addLabel("act-" + (i + 1), T + 0.46);
      acts[i + 1] = T + 0.46;

      /* In: copy first, then the panel rises through it */
      if (nxt.copy) tl.fromTo(nxt.copy, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.12, immediateRender: false }, T + 0.34);
      if (nxt.idx) tl.fromTo(nxt.idx, { autoAlpha: 0, y: 18 }, { autoAlpha: 1, y: 0, duration: 0.3, ease: "power3.out", immediateRender: false }, T + 0.36);
      if (nxt.words.length) {
        tl.fromTo(nxt.words, { yPercent: 118 }, { yPercent: 0, duration: 0.3, stagger: { amount: 0.2 }, ease: "power3.out", immediateRender: false }, T + 0.4);
      }
      if (nxt.lead) tl.fromTo(nxt.lead, { autoAlpha: 0, y: 18 }, { autoAlpha: 1, y: 0, duration: 0.36, ease: "power3.out", immediateRender: false }, T + 0.54);
      if (nxt.meter) tl.fromTo(nxt.meter, { autoAlpha: 0, y: 18 }, { autoAlpha: 1, y: 0, duration: 0.32, ease: "power3.out", immediateRender: false }, T + 0.62);
      if (nxt.actions) tl.fromTo(nxt.actions, { autoAlpha: 0, y: 18 }, { autoAlpha: 1, y: 0, duration: 0.3, ease: "power3.out", immediateRender: false }, T + 0.68);
      if (nxt.obj) {
        tl.fromTo(
          nxt.obj,
          { autoAlpha: 0, y: 64, scale: 0.97, filter: "blur(8px)" },
          { autoAlpha: 1, y: 0, scale: 1, filter: "blur(0px)", duration: 0.56, ease: "power3.out", immediateRender: false },
          T + 0.4
        );
      }

      pos = T + 1;
      tl.addLabel("ch-" + (i + 1), pos);
      points.push(pos);

      /* Operator notes: each quote is its own settle point */
      const quotes = nxt.stage.querySelectorAll(".home-quote");
      if (quotes.length) {
        for (let q = 0; q < quotes.length - 1; q++) {
          const QT = pos;
          tl.fromTo(
            quotes[q],
            { y: 0, autoAlpha: 1, filter: "blur(0px)" },
            { y: -40, autoAlpha: 0, filter: "blur(6px)", duration: 0.4, ease: "power2.in", immediateRender: false },
            QT
          );
          tl.fromTo(
            quotes[q + 1],
            { y: 48, autoAlpha: 0, filter: "blur(6px)" },
            { y: 0, autoAlpha: 1, filter: "blur(0px)", duration: 0.5, ease: "power3.out", immediateRender: false },
            QT + 0.42
          );
          pos = QT + 1;
          tl.addLabel("qs-" + (q + 1), pos);
          points.push(pos);
        }
      }
    }
    tl.set({}, {}, pos);

    pinNav.tl = tl;
    pinNav.st = tl.scrollTrigger;
    pinNav.points = points;
    pinNav.acts = acts;

    function activate(k: number) {
      if (k === activeDetail) return;
      const prev = parts[activeDetail];
      if (prev && prev.details) prev.details.reset();
      activeDetail = k;
      if (parts[k] && parts[k].details) parts[k].details!.play();
    }

    function onPinUpdate(self: any) {
      if (!pinNav.tl) return;
      const close = document.querySelector(".home-stage-end");
      if (close && close.getBoundingClientRect().top < window.innerHeight * 0.52) {
        setDot(7);
        return;
      }
      const time = self.progress * tl.duration();
      let active = 0;
      for (let k = 1; k < n; k++) if (time >= acts[k] - 0.0001) active = k;
      setDot(active);
      activate(active);
    }

    return () => {
      pin.classList.remove("is-active");
      if (pin.parentElement) pin.parentElement.classList.remove("is-pinned");
      pinNav.tl = null;
      pinNav.st = null;
      pinNav.points = [];
    };
  }

  /* Soft snap: after the user stops, glide to the nearest/next chapter */
  function maybeSnap(fromWheel?: boolean) {
    const { st, tl } = pinNav;
    if (!lenis || !st || !tl || snapBusy) return;
    if (fromWheel !== true && lenis.isScrolling) {
      snapTimer = later(() => maybeSnap(), 90);
      return;
    }
    const y = window.pageYOffset;
    if (y <= st.start + 2 || y >= st.end - 2) return;

    const dur = tl.duration() || 1;
    const pts = pinNav.points.map((t) => st.start + (t / dur) * (st.end - st.start));
    const seg = (st.end - st.start) / Math.max(1, pinNav.points[pinNav.points.length - 1]);
    let nearest = pts[0];
    for (let k = 1; k < pts.length; k++) if (Math.abs(pts[k] - y) < Math.abs(nearest - y)) nearest = pts[k];
    let target = nearest;
    if (Math.abs(nearest - y) > seg * 0.02) {
      if (scrollDir > 0) {
        for (let k = 0; k < pts.length; k++) {
          if (pts[k] > y + 1) {
            target = pts[k];
            break;
          }
        }
      } else {
        for (let k = pts.length - 1; k >= 0; k--) {
          if (pts[k] < y - 1) {
            target = pts[k];
            break;
          }
        }
      }
    }
    if (Math.abs(target - y) < 2) return;
    scrollToY(target, 1.0, true);
  }

  function watchSnap() {
    if (!lenis) return;
    /* Wheel intent is known the moment input pauses, no need to wait for the inertial tail. */
    window.addEventListener(
      "wheel",
      (e) => {
        if (!pinNav.st || snapBusy || e.ctrlKey || !e.deltaY) return;
        scrollDir = e.deltaY > 0 ? 1 : -1;
        lastWheel = Date.now();
        window.clearTimeout(snapTimer);
        snapTimer = later(() => maybeSnap(true), 110);
      },
      { passive: true, signal }
    );
    window.addEventListener(
      "scroll",
      () => {
        const y = window.pageYOffset;
        if (y !== lastScrollY) scrollDir = y > lastScrollY ? 1 : -1;
        lastScrollY = y;
        if (!pinNav.st || snapBusy || Date.now() - lastWheel < 600) return;
        window.clearTimeout(snapTimer);
        snapTimer = later(() => maybeSnap(), 170);
      },
      { passive: true, signal }
    );
  }

  /* ------------------------------------------------------------------
     Mobile / tablet: reveal on enter
     ------------------------------------------------------------------ */
  function revealOutsidePin() {
    gsap.utils.toArray<HTMLElement>(".home-stage").forEach((stage) => {
      if (stage.classList.contains("home-stage-hero")) return;
      if (stage.classList.contains("home-stage-end")) return;
      const copy = stage.querySelector<HTMLElement>(".home-copy");
      const obj = rightOf(stage);
      const heading = copy && copy.querySelector<HTMLElement>("h2");
      const words = heading ? splitWords(heading) : [];
      const idx = copy && copy.querySelector(".home-idx");
      const lead = copy && copy.querySelector(".lead");
      const meter = copy && copy.querySelector(".home-copy-meter");
      const actions = copy && copy.querySelector(".hero-actions");
      const details = buildDetails(stage);

      gsap.set(words, { yPercent: 118 });
      gsap.set([idx, lead, meter, actions].filter(Boolean), { autoAlpha: 0, y: 22 });
      if (obj) gsap.set(obj, { autoAlpha: 0, y: 48 });

      if (copy) {
        ScrollTrigger.create({
          trigger: copy,
          start: "top 86%",
          once: true,
          onEnter: () => {
            const tl = gsap.timeline({ defaults: { ease } });
            if (idx) tl.to(idx, { autoAlpha: 1, y: 0, duration: 0.9 }, 0);
            tl.to(words, { yPercent: 0, duration: 1.2, stagger: 0.07 }, 0.05);
            if (lead) tl.to(lead, { autoAlpha: 1, y: 0, duration: 1 }, 0.4);
            if (meter) tl.to(meter, { autoAlpha: 1, y: 0, duration: 0.9 }, 0.48);
            if (actions) tl.to(actions, { autoAlpha: 1, y: 0, duration: 1 }, 0.55);
          },
        });
      }
      if (obj) {
        ScrollTrigger.create({
          trigger: obj,
          start: "top 88%",
          once: true,
          onEnter: () => {
            gsap.to(obj, { autoAlpha: 1, y: 0, duration: 1.2, ease });
            if (details) gsap.delayedCall(0.35, details.play);
          },
        });
      }
    });
  }

  /* ------------------------------------------------------------------
     Closing section: time-based reveal on enter (the footer reveals itself)
     ------------------------------------------------------------------ */
  function revealClose() {
    const close = document.querySelector<HTMLElement>(".home-stage-end");
    if (!close) return;
    const words = splitWords(close.querySelector(".home-end > h2"));
    const idx = close.querySelectorAll(".home-idx");
    const lead = close.querySelectorAll(".home-end > .lead");
    const spec = close.querySelectorAll(".home-end-spec div");
    const btns = close.querySelectorAll(".home-close .btn");
    gsap.set(words, { yPercent: 120 });
    gsap.set([idx, lead, spec, btns], { autoAlpha: 0, y: 26 });
    ScrollTrigger.create({
      trigger: close,
      start: "top 78%",
      once: true,
      onEnter: () => {
        const tl = gsap.timeline({ defaults: { ease } });
        tl.to(idx, { autoAlpha: 1, y: 0, duration: 1 }, 0);
        tl.to(words, { yPercent: 0, duration: 1.6, stagger: 0.1 }, 0.1);
        tl.to(lead, { autoAlpha: 1, y: 0, duration: 1.2 }, 0.5);
        tl.to(spec, { autoAlpha: 1, y: 0, duration: 1.1, stagger: 0.08 }, 0.7);
        tl.to(btns, { autoAlpha: 1, y: 0, duration: 1.1, stagger: 0.08 }, 1);
      },
    });
    ScrollTrigger.create({
      trigger: close,
      start: "top 52%",
      onEnter: () => setDot(7),
      onEnterBack: () => setDot(7),
      onLeaveBack: () => setDot(6),
    });
  }

  /* Parallax "sheets": 07 slows and dims while the closing section slides over it. */
  function parallaxHome(k: number) {
    const { pass, cover } = makeParallax(k);
    if (window.innerWidth > 900) cover([".home-pin-inner"], ".home-stage-end", 0.5);
    cover([".home-stage-end .home-end", ".home-stage-end .home-end-bg"], ".site-footer", 0.5);
    pass(".home-stage-end", ".home-end-bg i:nth-child(1)", -90);
    pass(".home-stage-end", ".home-end-bg i:nth-child(2)", -50);
  }

  /* ------------------------------------------------------------------ boot */
  const mm = gsap.matchMedia();
  const ctx = gsap.context(() => {
    introHero();
    setupDots();
    watchSnap();

    mm.add("(min-width: 901px)", () => {
      const cleanup = pinSplitChapters();
      revealClose();
      parallaxHome(1);
      ScrollTrigger.refresh();
      return cleanup;
    });
    mm.add("(max-width: 900px)", () => {
      revealOutsidePin();
      revealClose();
      parallaxHome(0.5);
    });
  });
  cleanups.push(() => {
    mm.revert();
    ctx.revert();
  });

  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(() => {
      if (!signal.aborted) ScrollTrigger.refresh();
    });
  }

  return teardown();

  function teardown() {
    return () => {
      ac.abort();
      timers.forEach((id) => window.clearTimeout(id));
      timers.clear();
      cleanups.reverse().forEach((fn) => fn());
    };
  }
}
